import * as postRobot from "post-robot";
import { CardCaptureCallbacks, CardCaptureOptions } from "./model/config.model.ts";

const EVENT_GET_OPTIONS = "getOptions";
const EVENT_GET_CALLBACKS = "callbacks";

export type GetCardCaptureOptionsResponse = {
  token: string;
  options: CardCaptureOptions;
  parentHostName: string;
};

export interface CancelableChannel {
  cancel: () => void;
}

export function registerOptions(
  iframeWindow: Window,
  iframeDomain: string,
  options: CardCaptureOptions,
  token: string,
): CancelableChannel {
  const parentHostName = window.location.hostname;
  return postRobot.on(
    EVENT_GET_OPTIONS,
    {
      window: iframeWindow,
      domain: iframeDomain,
    },
    async (event): Promise<GetCardCaptureOptionsResponse> => {
      if (event.origin !== iframeDomain || event.source !== iframeWindow) {
        throw Error("Invalid origin or source.");
      }
      return {
        token,
        options,
        parentHostName,
      };
    },
  );
}

export async function requestOptions(): Promise<unknown> {
  if (!window.parent || window.parent === window) {
    throw Error("Page must be opened in an iframe.");
  }
  return postRobot.send(window.parent, EVENT_GET_OPTIONS).then((event) => event.data);
}

export function registerCallbacks(
  window: Window,
  domain: string,
  callbacks: CardCaptureCallbacks,
): CancelableChannel {
  return postRobot.on(
    EVENT_GET_CALLBACKS,
    {
      window,
      domain,
    },
    async (event): Promise<CardCaptureCallbacks> => {
      if (event.origin !== domain || event.source !== window) {
        throw Error("Invalid origin or source.");
      }
      return callbacks;
    },
  );
}

export async function requestCallbacks(): Promise<CardCaptureCallbacks> {
  if (!window.parent || window.parent === window) {
    if (import.meta.env.DEV) {
      // In development mode, we can return local callbacks for testing purposes
      return localDevelopmentCallbacks;
    }
    throw Error("Page must be opened in an iframe.");
  }
  return postRobot.send(window.parent, EVENT_GET_CALLBACKS).then((event) => {
    if (isCardCaptureCallbacks(event.data)) {
      return event.data;
    }
    throw new Error("Invalid callbacks response");
  });
}

function isCardCaptureCallbacks(callbacks: unknown): callbacks is CardCaptureCallbacks {
  return typeof callbacks === "object" && callbacks !== null;
}

const localDevelopmentCallbacks: CardCaptureCallbacks = {
  onTokenize: (response) => console.log("onTokenize callback called", response),
  onVerificationFailure: (response) =>
    console.error("onVerificationFailure callback called", response),
  onError: (error) => console.error("onError callback called", error),
  onCancel: () => console.log("onCancel callback called"),
  onSubmit: () => console.log("onSubmit callback called"),
  onPageLoaded: () => console.log("onPageLoaded callback called"),
};
